import React, { useCallback, useEffect, useState } from "react";
import { PageHeader, LoadingState, ErrorState, PhxButton } from "@/design-system";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const APPROVE_PERM = {
  statement: "portal.request.approve_statement",
  invoice: "portal.request.approve_invoice",
  order_copy: "portal.request.approve_order_copy",
};

/**
 * Staff approvals queue: client document requests and client-placed orders
 * waiting for a decision. Buttons only show when the user holds the matching
 * approve permission; the backend enforces it regardless.
 */
export default function ApprovalsPage() {
  const { hasPermission } = useAuth();
  const [requests, setRequests] = useState(null);
  const [orders, setOrders] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const orderStates = ["SUBMITTED", "UNDER_REVIEW"];
      const [r, ...os] = await Promise.all([
        api.get("/portal-requests", { params: { state: "pending" } }),
        ...orderStates.map((state) => (hasPermission("portal.order.approve")
          ? api.get("/orders/", { params: { state } })
          : Promise.resolve({ data: { items: [] } }))),
      ]);
      setRequests(r.data.items || []);
      setOrders(os.flatMap((o) => o.data.items || []).filter((x) => x.source === "CUSTOMER_PORTAL"));
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [hasPermission]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(null);
    }
  };

  const rejectRequest = (r) => {
    const note = window.prompt("Reason for rejecting (shown to the client):");
    if (!note || !note.trim()) return;
    act(r.id, () => api.post(`/portal-requests/${r.id}/reject`, { note: note.trim() }));
  };
  const rejectOrder = (o) => {
    const reason = window.prompt("Reason for rejecting this order:");
    if (!reason || !reason.trim()) return;
    act(o.id, () => api.post(`/orders/${o.id}/reject`, { version: o.version, reason: reason.trim() }));
  };

  if (error && requests === null) return <ErrorState message={error} retry={load} testid="approvals-error" />;
  if (requests === null) return <LoadingState testid="approvals-loading" />;

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approvals" />
      {error && <p className="text-sm text-red-600 mb-2" role="alert">{error}</p>}

      <h2 className="text-sm font-semibold uppercase tracking-wide mb-2">Client document requests</h2>
      {requests.length === 0 ? (
        <p className="text-sm text-slate-500 mb-6" data-testid="no-requests">Nothing waiting.</p>
      ) : (
        <ul className="divide-y border bg-white mb-6">
          {requests.map((r) => (
            <li key={r.id} className="flex items-center justify-between p-3" data-testid={`request-${r.id}`}>
              <span className="text-sm">
                <span className="font-medium">{r.party_name || r.party_id}</span>{" "}
                wants {String(r.type).replace("_", " ")} ({String(r.format).toUpperCase()})
              </span>
              {hasPermission(APPROVE_PERM[r.type]) ? (
                <span className="flex gap-2">
                  <PhxButton disabled={busy === r.id}
                    onClick={() => act(r.id, () => api.post(`/portal-requests/${r.id}/approve`))}>
                    Approve
                  </PhxButton>
                  <PhxButton variant="secondary" disabled={busy === r.id} onClick={() => rejectRequest(r)}>
                    Reject
                  </PhxButton>
                </span>
              ) : (
                <span className="text-xs text-slate-500">No permission</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {hasPermission("portal.order.approve") && (
        <>
          <h2 className="text-sm font-semibold uppercase tracking-wide mb-2">Client orders pending approval</h2>
          {orders.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="no-orders">Nothing waiting.</p>
          ) : (
            <ul className="divide-y border bg-white">
              {orders.map((o) => (
                <li key={o.id} className="flex items-center justify-between p-3" data-testid={`order-${o.id}`}>
                  <span className="text-sm font-medium">{o.number || o.id}</span>
                  <span className="flex gap-2">
                    <PhxButton disabled={busy === o.id}
                      onClick={() => act(o.id, () => api.post(`/orders/${o.id}/confirm`,
                        { version: o.version, reserve_stock: true }))}>
                      Accept
                    </PhxButton>
                    <PhxButton variant="secondary" disabled={busy === o.id} onClick={() => rejectOrder(o)}>
                      Reject
                    </PhxButton>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
